import React, { Dispatch, SetStateAction, useMemo, useRef, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import { useRoleGuardHook } from "hooks";
import { DocumentQuestion, DocumentSection, QuestionAiStatus } from "store/types";

// import { useDocumentSectionSelect } from "store";
import { RoleName } from "../../../constants";
import { Question } from "./Question";

interface Props {
  section: DocumentSection;
  questions: DocumentQuestion[];
  documentId: number;
  createQuestion: () => Promise<DocumentQuestion>;
  deleteQuestion: (id: number) => Promise<void> | void;
  setPendingAIQuestionIds: Dispatch<SetStateAction<number[]>>;
  pendingAIQuestionIds: number[];
  hideAddQuestion: boolean;
}

export const QuestionsSet: React.FC<Props> = ({
  questions,
  documentId,
  createQuestion,
  deleteQuestion,
  section,
  setPendingAIQuestionIds,
  pendingAIQuestionIds,
  hideAddQuestion = false
}) => {
  // const section = useDocumentSectionSelect({ documentId, sectionId: sectionId });
  const [menuQuestionId, setMenuQuestionId] = useState<number>(0);
  const lastQuestionRef = useRef<HTMLInputElement | null>(null);
  const ref: React.MutableRefObject<SVGSVGElement | null> = useRef(null);

  const handleOpenMenu = (e: React.MouseEvent, questionId: number) => {
    e.stopPropagation();
    ref.current = e.target as SVGSVGElement;
    setMenuQuestionId(questionId);
  };

  const addSectionEnabled = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst]);

  const lastQuestionHasText: boolean = useMemo(() => {
    if (!questions.length) return false;

    if (!questions[questions.length - 1].question) return false;

    return true;
  }, [questions]);

  const createQuestionAndFocus = async () => {
    await createQuestion();
    // Move "focus" last question to the end of the queue to let browser render new question only then focus it
    setTimeout(() => {
      if (!lastQuestionRef.current) return;
      lastQuestionRef.current.focus();
    }, 100);
  };

  return (
    <>
      <Stack sx={{ flexGrow: 1 }}>
        {questions.map((question, index, array) => (
          <Question
            key={question.id}
            question={question}
            documentId={documentId}
            handleOpenMenu={handleOpenMenu}
            createQuestionAndFocus={createQuestionAndFocus}
            questionsLength={questions.length}
            {...(index + 1 === questions.length && { inputRef: lastQuestionRef })}
            disableBM={array.length === index + 1}
            setPendingAIQuestionIds={setPendingAIQuestionIds}
            pendingAIQuestionIds={pendingAIQuestionIds}
            disable={hideAddQuestion}
            section={section}
          />
        ))}
        {addSectionEnabled &&
          lastQuestionHasText &&
          section?.ai_status !== QuestionAiStatus.InProgress &&
          !hideAddQuestion && (
            <Button
              sx={{ width: "fit-content", color: "#5d7782", fontWeight: 300, marginTop: "10px" }}
              disableRipple
              onClick={async () => {
                createQuestionAndFocus();
              }}
            >
              Add Question
            </Button>
          )}
      </Stack>
      <Menu anchorEl={ref.current} open={!!menuQuestionId} onClose={() => setMenuQuestionId(0)}>
        <MenuItem
          onClick={() => {
            deleteQuestion(menuQuestionId);
            setMenuQuestionId(0);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="md" sx={{ fill: "#0a3142" }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
